import { Empty, Spin, Tabs } from "antd";

import { PortfolioPageShell } from "@/views/Portfolio/shared";
import ConnectionCard, {
  CardProps,
} from "@/components/Connections/ConnectionCard";
import FeedCompositionTable from "@/components/PortfolioConnections/FeedCompositionTable";
import { usePortfolioConnectionsQuery } from "@/hooks/usePortfolioConnectionsQuery";

import { getTenantExternalConnectionsConfig } from "./portfolioConnectionsConfig";

/**
 * Portfolio → Data. Lists the sources external to the trials — public
 * registries and commercial reference feeds, the same data whichever trial you
 * are looking at. A trial's own conduct feeds (EDC, labs, eCOA, SAE, CTMS,
 * imaging) are per-trial and live on that trial's Data → Configuration screen;
 * rolled up here they were a wall of near-duplicate cards. The exception is a
 * tenant whose feeds come as one bundle spanning every trial (florence's
 * eBinders export): those are listed here too.
 */
export default function PortfolioConnections() {
  const { connections: fallbackConnections, composition } =
    getTenantExternalConnectionsConfig(window.location.hostname);
  const { data: seededConnections, isLoading } = usePortfolioConnectionsQuery();

  // Prefer the tenant schema's seeded data_connection rows (the endpoint
  // returns external sources only); fall back to the static per-tenant set for
  // schemas that have not been seeded yet.
  const connections: CardProps[] = seededConnections?.length
    ? seededConnections
    : fallbackConnections;

  const tabItems = [
    {
      key: "connections",
      label: "Connections",
      children: isLoading ? (
        <div className="flex justify-center pt-12">
          <Spin />
        </div>
      ) : (
        <ExternalConnections connections={connections} />
      ),
    },
    ...(composition
      ? [
          {
            key: "composition",
            label: "Feed Composition",
            children: (
              <div className="pt-4">
                <FeedCompositionTable rows={composition} />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <PortfolioPageShell
      title="Portfolio: Data"
      tooltipText="View the data sources feeding this portfolio. Each trial's own data connections are on that trial's Data > Configuration page."
    >
      <Tabs defaultActiveKey="connections" items={tabItems} />
    </PortfolioPageShell>
  );
}

function ExternalConnections({ connections }: { connections: CardProps[] }) {
  if (connections.length === 0) {
    // Says why the tab is empty and where the cards went — a tenant whose
    // feeds all carry trial conduct data has no external sources at all.
    return (
      <div
        data-testid="portfolio-connections-empty"
        className="flex justify-center pt-12"
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              No external data sources are configured for this portfolio.
              <br />
              Each trial&apos;s own data connections are on that trial&apos;s
              Data &gt; Configuration page.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div
      data-testid="portfolio-connections-panel"
      className="grid grid-cols-4 gap-[15px] pt-4"
    >
      {connections.map((conn) => (
        <ConnectionCard key={conn.datasource_type} card={conn} />
      ))}
    </div>
  );
}
